import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Plus, Send, Ban, RefreshCw, Download, ChevronRight, Search, ExternalLink,
  IdCard, CheckCircle2, XCircle, Banknote, Eye,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";

function AdminGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

const STATUS_OPTS = [
  { v: "", l: "All" }, { v: "pending", l: "Pending" }, { v: "viewed", l: "Viewed" },
  { v: "completed", l: "Signed" }, { v: "declined", l: "Declined" },
  { v: "voided", l: "Voided" }, { v: "expired", l: "Expired" },
];

function IdBadge({ status }) {
  if (!status) return null;
  const map = {
    submitted: ["bg-amber-100 text-amber-700", "Pending review"],
    verified: ["bg-emerald-100 text-emerald-700", "ID verified"],
    rejected: ["bg-red-100 text-red-700", "ID rejected"],
  };
  const [cls, label] = map[status] || ["bg-black/10", status];
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${cls} flex items-center gap-1`}>
      <IdCard className="w-3 h-3" /> {label}
    </span>
  );
}

function PaymentAuthsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const r = await api.get("/admin/payment-authorizations");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid="admin-payment-auths">
      <h3 className="font-serif text-2xl mb-4 flex items-center gap-2">
        <Banknote className="w-5 h-5 text-[var(--gold)]" /> Payment Authorizations
      </h3>
      {loading ? (
        <p className="text-sm text-black/40">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-black/40">No ACH authorizations yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-black/50">
              <tr className="text-left">
                <th className="py-2 pr-3">Auth ID</th>
                <th className="py-2 pr-3">Holder</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Account</th>
                <th className="py-2 pr-3">Recurring</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.auth_id} className="border-t border-[var(--line)]" data-testid={`payauth-row-${r.auth_id}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{r.auth_id}</td>
                  <td className="py-2 pr-3">{r.account_holder}</td>
                  <td className="py-2 pr-3">${Number(r.amount).toFixed(2)}</td>
                  <td className="py-2 pr-3 font-mono">****{r.account_last4}</td>
                  <td className="py-2 pr-3">{r.recurring ? "Yes" : "No"}</td>
                  <td className="py-2 pr-3">{r.status}</td>
                  <td className="py-2 text-xs text-black/50">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminAgreementsPage() {
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");

  // create form
  const [creating, setCreating] = useState(false);
  const [tpl, setTpl] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  async function load() {
    try {
      const [t, list] = await Promise.all([
        api.get("/admin/agreements/templates"),
        api.get(`/admin/agreements${filter ? `?status=${filter}` : ""}`),
      ]);
      setTemplates(t.data || []);
      setRows(list.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    if (!tpl || !email || !name) return toast.error("Template, email, name required.");
    setCreating(true);
    try {
      const r = await api.post("/admin/agreements", { template_key: tpl, email, name });
      toast.success("Agreement sent to " + email);
      setTpl(""); setEmail(""); setName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCreating(false); }
  }

  async function voidAgreement(aid) {
    if (!window.confirm("Void this agreement? Cannot undo.")) return;
    try { await api.post(`/admin/agreements/${aid}/void`); toast.success("Voided"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function resend(aid) {
    try { await api.post(`/admin/agreements/${aid}/resend`); toast.success("Resent"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function decideId(aid, decision) {
    try {
      await api.post(`/admin/agreements/${aid}/id-verify/${decision}`);
      toast.success(`ID ${decision}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filtered = query
    ? rows.filter((r) => (r.title + r.assigned_email + r.assigned_name).toLowerCase().includes(query.toLowerCase()))
    : rows;

  return (
    <AdminGate>
      <div data-testid="admin-agreements-page" className="space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin · Legal</p>
          <h1 className="font-serif font-light text-4xl">Agreements</h1>
          <p className="text-[var(--muted)] text-sm mt-2">Send, track, void, and audit electronic agreements.</p>
        </div>

        {/* Send a new agreement */}
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid="admin-agreements-create">
          <h3 className="font-serif text-2xl mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-[var(--gold)]" /> Send agreement
          </h3>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <select value={tpl} onChange={(e) => setTpl(e.target.value)}
              className="px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
              data-testid="agr-template-select">
              <option value="">Choose template…</option>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
            </select>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Recipient email" type="email"
              className="px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
              data-testid="agr-recipient-email" />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Recipient name"
              className="px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
              data-testid="agr-recipient-name" />
          </div>
          <button onClick={create} disabled={creating} className="btn-gold" data-testid="agr-send">
            <Send className="w-4 h-4" /> {creating ? "Sending…" : "Send for signature"}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[var(--line)] rounded-2xl p-5 flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title or email…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--line)] text-sm outline-none focus:border-[var(--gold)]"
              data-testid="agr-search" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-[var(--line)] text-sm">
            {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>

        {/* List */}
        <div className="space-y-2" data-testid="admin-agreements-list">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-black/40">No agreements in this view.</div>
          ) : filtered.map((a) => (
            <div key={a.agreement_id} className="bg-white border border-[var(--line)] rounded-xl p-4 flex items-center gap-3 flex-wrap"
              data-testid={`admin-agreement-row-${a.agreement_id}`}>
              <FileText className="w-5 h-5 text-[var(--gold)]" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg truncate">{a.title}</p>
                <p className="text-xs text-black/50">{a.assigned_email} · {a.assigned_name}</p>
                <p className="text-[10px] text-black/40 font-mono mt-0.5">{a.agreement_id} · v{a.version} · {a.audit_count} events</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                  a.status === "completed" ? "bg-emerald-100 text-emerald-700"
                  : a.status === "viewed" ? "bg-blue-100 text-blue-700"
                  : a.status === "voided" ? "bg-black/10 text-black/60"
                  : a.status === "declined" ? "bg-red-100 text-red-600"
                  : "bg-amber-100 text-amber-700"
                }`}>{a.status}</span>
                <IdBadge status={a.id_verification_status} />
              </div>
              <div className="flex gap-2 flex-wrap">
                {a.id_verification_status === "submitted" && (
                  <>
                    <a href={`${BACKEND_URL}/api/admin/agreements/${a.agreement_id}/id-image`} target="_blank" rel="noopener"
                      className="text-xs px-3 py-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                      data-testid={`id-view-${a.agreement_id}`}>
                      <Eye className="w-3 h-3 inline" /> View ID
                    </a>
                    <button onClick={() => decideId(a.agreement_id, "approve")}
                      className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`id-approve-${a.agreement_id}`}>
                      <CheckCircle2 className="w-3 h-3 inline" /> Approve
                    </button>
                    <button onClick={() => decideId(a.agreement_id, "reject")}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`id-reject-${a.agreement_id}`}>
                      <XCircle className="w-3 h-3 inline" /> Reject
                    </button>
                  </>
                )}
                {a.status === "completed" && (
                  <a href={`${BACKEND_URL}/api/agreements/${a.agreement_id}/pdf`} target="_blank" rel="noopener"
                    className="text-xs px-3 py-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                    data-testid={`agr-pdf-${a.agreement_id}`}>
                    <Download className="w-3 h-3 inline" />
                  </a>
                )}
                {a.status !== "completed" && a.status !== "voided" && (
                  <>
                    <button onClick={() => resend(a.agreement_id)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                      data-testid={`agr-resend-${a.agreement_id}`}>
                      <RefreshCw className="w-3 h-3 inline" />
                    </button>
                    <button onClick={() => voidAgreement(a.agreement_id)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`agr-void-${a.agreement_id}`}>
                      <Ban className="w-3 h-3 inline" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <PaymentAuthsPanel />
      </div>
    </AdminGate>
  );
}
